'use client'

import { useLayoutEffect } from 'react'
import type { Route } from 'next'
import { usePathname, useRouter } from 'next/navigation'
import { Button } from '@/components/ui/button'
import { cn } from '@/lib/utils'
import { routes } from '@/config/routes'
import { useTourStore } from '@/features/investor/store/tour-store'
import {
  BarsGraphic,
  ChecklistGraphic,
  HelpGraphic,
  PulseGraphic,
  SwapGraphic,
  TargetGraphic,
  WelcomeGraphic,
} from '@/features/investor/components/tour/tour-graphics'
import '@/components/patterns/dashboard-tour.css'

/**
 * Each step visits the real tab and spotlights its real nav item, rather than describing it in
 * the abstract. Ported from `nivex-design/apps/nivex-web-v3/src/components/DashboardTour.tsx`,
 * including its Goals step now that Build My Portfolio has a real dashboard tab (`/dashboard/build`).
 */
const STEPS = [
  {
    path: null,
    target: null,
    title: 'Welcome to Nivex',
    description:
      "A quick look at where everything lives, so you're never hunting for something. Takes about a minute.",
    Graphic: WelcomeGraphic,
  },
  {
    path: routes.investor.home,
    target: 'nav-home',
    title: 'Your dashboard',
    description:
      'This is a live snapshot of your money — it updates as our team works on your account, so what you see here is always current.',
    Graphic: PulseGraphic,
  },
  {
    path: routes.investor.portfolio,
    target: 'nav-portfolio',
    title: 'Portfolio',
    description:
      'Your full portfolio breakdown — holdings, valuations, and how it has moved over time.',
    Graphic: BarsGraphic,
  },
  {
    path: routes.investor.auditPrescription,
    target: 'nav-audit',
    title: 'Audit & Prescription',
    description:
      "Your advisor's current guidance lives here, in plain language — what to trim, what to add, and why.",
    Graphic: ChecklistGraphic,
  },
  {
    path: routes.investor.trades,
    target: 'nav-trades',
    title: 'Trades',
    description:
      'Every recorded trade, your win rate, and net P&L, if you use our trading review service.',
    Graphic: SwapGraphic,
  },
  {
    path: routes.investor.build,
    target: 'nav-build',
    title: 'Goals',
    description:
      'The goals you told us about and the plan built around them — see how each is tracking.',
    Graphic: TargetGraphic,
  },
  {
    path: null,
    target: 'help',
    title: 'Come back anytime',
    description: 'This icon reopens the tour whenever you want to walk through it again.',
    Graphic: HelpGraphic,
  },
] as const

function computeTooltipPosition(rect: {
  top: number
  left: number
  width: number
  height: number
}) {
  const margin = 16
  const tooltipWidth = 320
  const viewportW = window.innerWidth
  const viewportH = window.innerHeight
  const right = rect.left + rect.width
  const bottom = rect.top + rect.height

  let left = right + margin
  let top = rect.top

  if (left + tooltipWidth > viewportW - margin) {
    left = Math.min(Math.max(rect.left, margin), viewportW - tooltipWidth - margin)
    top = bottom + margin
  }
  top = Math.min(top, viewportH - 220)
  top = Math.max(top, margin)

  return { top, left }
}

export function DashboardTour() {
  const open = useTourStore((s) => s.open)
  const step = useTourStore((s) => s.step)
  const setStep = useTourStore((s) => s.setStep)
  const closeTour = useTourStore((s) => s.closeTour)
  // Read + write through the store, not local state: the shell (and this
  // component with it) fully remounts on every route change, so the ring
  // keeps animating from its last real position instead of vanishing to a
  // full backdrop and popping back in.
  const rect = useTourStore((s) => s.lastRect)
  const setLastRect = useTourStore((s) => s.setLastRect)
  const router = useRouter()
  const pathname = usePathname()

  const current = STEPS[Math.min(Math.max(step, 0), STEPS.length - 1)] ?? STEPS[0]
  // Briefly true right after a step change that targets a different page, in the instant between
  // calling `router.push` and that navigation actually landing (this same component instance, or
  // its remounted replacement, re-renders with the new step before `pathname` has caught up).
  // Used only to skip spotlight measurement during that gap — navigation itself is triggered
  // directly by `goToStep`, not reactively here, so it doesn't depend on a remount to fire.
  const waitingOnNav = Boolean(current.path && pathname !== current.path)

  const goToStep = (index: number) => {
    const target = STEPS[index]
    if (!target) return
    setStep(index)
    if (target.path && target.path !== pathname) router.push(target.path as Route)
  }

  // Completing the tour (its last step, "Come back anytime", has no `path` of its own) previously
  // left the investor wherever the second-to-last step's navigation happened to land them (the
  // Trades tab) instead of returning them to the dashboard they started from. Skip is left
  // unchanged — abandoning the tour mid-way isn't "finishing" it, so it doesn't redirect.
  const finishTour = () => {
    closeTour()
    if (pathname !== routes.investor.home) router.push(routes.investor.home as Route)
  }

  useLayoutEffect(() => {
    if (!open || waitingOnNav) return
    if (!current.target) {
      setLastRect(null)
      return
    }
    const measure = () => {
      const matches = [...document.querySelectorAll(`[data-tour="${current.target}"]`)]
      const visible = matches
        .map((el) => el.getBoundingClientRect())
        .find((r) => r.width > 0 && r.height > 0)
      if (visible) {
        setLastRect({
          top: visible.top,
          left: visible.left,
          width: visible.width,
          height: visible.height,
        })
      }
    }
    // Sidebar width settles a beat after mount (cookie-derived collapsed/expanded state, font
    // load, route transition) — one rAF is sometimes too early, so retry a few times over the
    // first half-second rather than measuring once.
    const timeouts = [0, 50, 150, 300, 600].map((ms) => setTimeout(measure, ms))
    window.addEventListener('resize', measure)
    window.addEventListener('scroll', measure, true)
    return () => {
      timeouts.forEach(clearTimeout)
      window.removeEventListener('resize', measure)
      window.removeEventListener('scroll', measure, true)
    }
  }, [open, waitingOnNav, current.target, setLastRect])

  if (!open) return null

  const isLast = step === STEPS.length - 1
  const Graphic = current.Graphic
  const tooltipStyle = rect ? computeTooltipPosition(rect) : null

  return (
    <>
      <div className="pointer-events-none fixed inset-0 z-[100]">
        {rect ? (
          <div
            className="pointer-events-none fixed rounded-xl transition-all duration-300 ease-out"
            style={{
              top: rect.top - 6,
              left: rect.left - 6,
              width: rect.width + 12,
              height: rect.height + 12,
              boxShadow:
                '0 0 0 9999px rgba(10,10,32,0.55), 0 0 0 2px var(--primary), 0 0 0 5px color-mix(in srgb, var(--primary) 25%, transparent)',
            }}
          />
        ) : (
          <div className="fixed inset-0 bg-black/55 transition-opacity duration-300 ease-out" />
        )}
      </div>

      <div
        className="bg-background fixed z-[101] w-[320px] rounded-xl border p-4 shadow-xl transition-all duration-300 ease-out"
        style={tooltipStyle ?? { top: '50%', left: '50%', transform: 'translate(-50%, -50%)' }}
      >
        <div
          key={step}
          className="flex items-start gap-3"
          style={{ animation: 'tour-fade-in 0.25s ease-out' }}
        >
          <Graphic />
          <div className="flex-1 pt-0.5">
            <p className="text-sm font-semibold">{current.title}</p>
            <p className="text-muted-foreground mt-1 text-xs leading-relaxed">
              {current.description}
            </p>
          </div>
        </div>

        <div className="mt-3 flex items-center justify-center gap-1.5">
          {STEPS.map((_, i) => (
            <span
              key={i}
              className={cn(
                'h-1.5 w-1.5 rounded-full transition-colors',
                i === step ? 'bg-primary' : 'bg-muted',
              )}
            />
          ))}
        </div>

        <div className="mt-3 flex items-center justify-between">
          <Button variant="ghost" size="sm" onClick={closeTour}>
            Skip
          </Button>
          <div className="flex gap-2">
            {step > 0 && (
              <Button variant="outline" size="sm" onClick={() => goToStep(step - 1)}>
                Back
              </Button>
            )}
            <Button size="sm" onClick={() => (isLast ? finishTour() : goToStep(step + 1))}>
              {isLast ? 'Done' : 'Next'}
            </Button>
          </div>
        </div>
      </div>
    </>
  )
}
